import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const formUrl = 'https://readdy.ai/api/form/d9opol8senbniolesjgg';
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91免费',
    description: '91免费专注于免费看片软件推荐与下载，为用户提供最新最全的免费看片app和视频软件资源，覆盖安卓、苹果、电脑全平台。',
    url: 'https://91free.example.com/about',
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb />

            <section className="mb-16">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">关于91免费</h1>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    <strong>91免费</strong>成立于2020年，是国内领先的免费看片软件推荐与下载平台。我们致力于为用户提供最新、最全、最安全的免费视频软件资源，帮助每一位影视爱好者找到最适合自己的观影工具。
                  </p>
                  <p>
                    经过多年的积累和发展，91免费已经收录了超过50款经过严格筛选的免费看片软件，覆盖安卓、苹果、Windows、Mac、电视盒子等全平台设备。我们的编辑团队坚持每周更新推荐列表，确保用户获取的始终是最新、最好用的软件信息。
                  </p>
                  <h2 className="text-lg font-bold text-foreground-950 mt-6 mb-3">我们的使命</h2>
                  <p>
                    让每一个人都能轻松找到安全、流畅、免费的观影方式。在91免费，我们相信优质的影视内容应该被每个人自由地欣赏。通过提供专业的软件评测、详细的安装教程和及时的资源更新，我们帮助用户跨越技术门槛，畅享免费看片的乐趣。
                  </p>
                  <h2 className="text-lg font-bold text-foreground-950 mt-6 mb-3">核心优势</h2>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>严格筛选：</strong>每款推荐软件均经过安全检测和实际使用测试，确保无病毒、无恶意广告</li>
                    <li><strong>全平台覆盖：</strong>安卓、苹果、Windows、Mac、电视盒子，一个平台满足所有设备需求</li>
                    <li><strong>持续更新：</strong>每周更新推荐列表，第一时间上线优质新软件，及时下架失效产品</li>
                    <li><strong>详尽教程：</strong>每款软件配有图文并茂的安装使用教程，新手也能轻松上手</li>
                    <li><strong>用户至上：</strong>认真倾听用户反馈，不断优化推荐质量和服务体验</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((item, index) => (
                  <div key={index} className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${i < item.rating ? 'text-accent-500' : 'text-background-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {item.content}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground-950">{item.name}</span>
                      <span className="text-xs text-foreground-400">{item.location} · {item.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">support@91free.example.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-customer-service-2-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">在线客服</h3>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">北京市朝阳区建国路88号</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <h3 className="text-base font-semibold text-foreground-950 mb-5">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您想咨询的内容（500字以内）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="website_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </div>

                    {formStatus === 'success' && (
                      <div className="mt-4 p-3 rounded-lg bg-secondary-50 text-secondary-700 text-sm text-center">
                        消息已发送成功，我们会尽快回复您！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="mt-4 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm text-center">
                        {formError || '发送失败，请稍后再试'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <Footer />
    </>
  );
}